// Constants
import { NetworkError } from "../../api/rest/network-errors"

// Cookie storage
import { get } from "@/lib/cookies"

// Environment variables
import { isDevelopment, isProduction } from "@/utils/helpers"
import { API_URL, STORYBOOK_API_URL } from "@/lib/env"

// Sentry
import { captureMessage } from "@sentry/react"

const normaliseApiUrl = (url: string = "") => {
	if (isProduction() && !url) {
		throw new Error(NetworkError.NO_API_URL)
	}

	// make sure no slash is present at the end of the url
	const withSlash = url.replace(/\/$/, "")

	// in production we force https
	// so the url starts with https://
	if (!isDevelopment()) {
		if (!url.includes("127.0.0.1")) {
			return withSlash.replace(/^(https?:\/\/)?/, "https://")
		}
	}

	return withSlash
}

const apiUrlOverride = () => {
	try {
		if (typeof window !== "undefined") {
			return new URL(window.location.href).searchParams.get("mockapi")
		}
	} catch {}
	return ""
}

const apiUrl = normaliseApiUrl(apiUrlOverride() || API_URL || STORYBOOK_API_URL)

/**
 * Build a Headers object for the fetch call: merge incoming headers, apply
 * defaults, attach the Django CSRF token, and drop Content-Type for FormData
 * bodies so the browser can set the multipart boundary.
 */
function buildHeaders(
	incomingHeaders: HeadersInit | undefined,
	body: BodyInit | null | undefined,
): Headers {
	const fetchHeaders = new Headers()

	if (incomingHeaders) {
		const headerEntries =
			incomingHeaders instanceof Headers
				? incomingHeaders.entries()
				: Array.isArray(incomingHeaders)
					? incomingHeaders
					: Object.entries(incomingHeaders)

		for (const [key, value] of headerEntries) {
			fetchHeaders.set(key, value)
		}
	}

	if (!fetchHeaders.has("accept")) {
		fetchHeaders.set("Accept", "application/json")
	}

	// TODO: This hack is here to allow cookies from Django to work
	fetchHeaders.set("X-CSRFToken", get("csrftoken") || "")

	if (!fetchHeaders.has("content-type")) {
		fetchHeaders.set("Content-Type", "application/json")
	}

	// FormData bodies: drop the default `application/json` set above so the
	// browser can attach `multipart/form-data` with the correct boundary.
	if (body instanceof FormData) {
		fetchHeaders.delete("Content-Type")
	}

	return fetchHeaders
}

/**
 * Handle error responses (http status >= 400):
 *   - 4xx: parse the JSON body and throw it as the error
 *   - 5xx: report to Sentry and throw the raw response
 */
async function handleErrorResponse(response: Response): Promise<never> {
	if (response.status >= 400 && response.status < 500) {
		const json = await response.json()
		throw json
	}
	reportRestErrors(response)
	throw response
}

// TODO: Add handling exception responses (http response.status codes)
const _api = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
	const { method, headers: incomingHeaders, body, signal } = options

	const fetchHeaders = buildHeaders(incomingHeaders, body)
	const requestUrl = `${apiUrl}${url}`

	const response = await fetch(requestUrl, {
		method,
		body,
		headers: fetchHeaders,
		signal,
		credentials: "include", // Send cookies
		mode: "cors", // Allow connecting to API on different domain
	})

	if (!response.ok) {
		await handleErrorResponse(response)
	}

	// avoid JSON processing for "No Content" responses
	if (response.status === 204) {
		return {} as T
	}

	return response.json()
}

/**
 * Report REST errors to Sentry
 * @param response
 */
function reportRestErrors(response: Response) {
	// All other errors, log and report
	console.error(response)

	// Capture message in Sentry
	captureMessage(response.type, {
		level: "error",
		extra: {
			type: response.type,
			body: response.body,
			headers: response.headers,
			status: response.status,
			url: response.url,
		},
	})
}

export const apiWithTokenAuth = async <T>(
	url: string,
	options: RequestInit = {},
) => {
	return _api<T>(url, options)
}

export const api = async <T>(url: string, options: RequestInit = {}) => {
	return _api<T>(url, options)
}

export default api
